import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../utils/axios-instance";
import toast from "react-hot-toast";
import ClientActionToolbar from "../components/client/ClientActionToolbar";
import ClientFilePreviewer from "../components/client/ClientFilePreviewer";
import RejectionModal from "../components/client/RejectionModal";
import ApproveConfirmModal from "../components/client/ApproveConfirmModal";
import PoweredByPill from "../components/client/PoweredByPill";
import DeliverableFilterBar, {
  filterDeliverables,
} from "../components/deliverables/DeliverableFilterBar";
import DeliverableChat from "../components/client/DeliverableChat";
import Loading from "../components/Loading";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { fileIcons } from "../utils/icon-mimetypes";
import FileBg from "../generators/fileBGGenerator";
import { DEFAULT_DEPARTMENT_COLORS } from "../constants/defaultDepartments";
import { resolveDeliverableMimetype } from "../components/vendors/deliverables/utils";

const GRID_STATUS_STYLES = {
  pending: { bg: "#fff7e6", text: "#b45309", label: "Pending" },
  approved: { bg: "#e6f4ea", text: "#137333", label: "Approved" },
  rejected: { bg: "#fce8e6", text: "#c5221f", label: "Rejected" },
  downloaded: { bg: "#e0f2fe", text: "#0369a1", label: "Downloaded" },
};

const DEFAULT_FILE_ICON = `${process.env.PUBLIC_URL}/assets/defaulticon.webp`;

const hexToRgba = (hex, alpha) => {
  let r = 0,
    g = 0,
    b = 0;
  if (!hex) return `rgba(0,0,0,${alpha})`;
  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  } else if (hex.length === 7) {
    r = parseInt(hex[1] + hex[2], 16);
    g = parseInt(hex[3] + hex[4], 16);
    b = parseInt(hex[5] + hex[6], 16);
  }
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Picks one of the default department colours per deliverable. Seeded by id
// so the choice looks random across the grid but stays the same for a given
// file across re-renders and reloads.
const pickCardColor = (id = "") => {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  }
  return DEFAULT_DEPARTMENT_COLORS[hash % DEFAULT_DEPARTMENT_COLORS.length];
};

// Stored mimetype first; when it's missing or the generic octet-stream (the
// browser couldn't detect the type at upload), fall back to the file
// extension, then to the default icon.
const getDeliverableIcon = (item) => {
  const stored = item.metadata?.mimetype || item.mimetype;
  const mime =
    stored && stored !== "application/octet-stream"
      ? stored
      : resolveDeliverableMimetype({ name: item.name });
  return fileIcons[mime] || DEFAULT_FILE_ICON;
};

export default function ClientDeliverablePage({ darkMode }) {
  const { roomId, deliverableId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // The public review link carries a passcode/token query param that gates
  // access for this anonymous client — required by the message-send endpoint.
  const passcode = searchParams.get("token") || searchParams.get("passcode");

  // --- Fast path: the single deliverable this route was opened for.
  // Fetching this blocks only the focus preview modal, never the page.
  const [focusDeliverable, setFocusDeliverable] = useState(null);
  const [loadingFocus, setLoadingFocus] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [status, setStatus] = useState("pending");
  const [isPaid, setIsPaid] = useState(false);
  const [isRejectionModalOpen, setIsRejectionModalOpen] = useState(false);
  const [isSubmittingRejection, setIsSubmittingRejection] = useState(false);
  const [rejectionTarget, setRejectionTarget] = useState(null);
  // "reject" or "changes" — both submit the same rejection payload, only the
  // modal copy differs.
  const [rejectionVariant, setRejectionVariant] = useState("reject");
  const [approveTarget, setApproveTarget] = useState(null);
  const [isSubmittingApproval, setIsSubmittingApproval] = useState(false);
  const [isFileCommentsOpen, setIsFileCommentsOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("All");

  // --- Background: the full room grid, loaded independently and never
  // blocks the focus modal above from opening.
  const [roomDeliverables, setRoomDeliverables] = useState([]);
  const [loadingRoomList, setLoadingRoomList] = useState(true);

  const theme = {
    bg: darkMode ? "#111827" : "#f9fafb",
    cardBg: darkMode ? "#1f2937" : "#ffffff",
    border: darkMode ? "#374151" : "#e5e7eb",
    textMain: darkMode ? "#f9fafb" : "#111827",
    textSub: darkMode ? "#9ca3af" : "#6b7280",
  };

  // 1. Fast-path fetch of the target deliverable — auto-opens the modal
  // as soon as it resolves, independent of the background grid fetch.
  useEffect(() => {
    let cancelled = false;

    // Entered at the room level (no deliverableId) — nothing to focus, so
    // skip straight to the room grid rather than fetching "undefined".
    if (!deliverableId) {
      setFocusDeliverable(null);
      setIsModalOpen(false);
      setLoadingFocus(false);
      return () => {
        cancelled = true;
      };
    }

    const fetchFocusDeliverable = async () => {
      try {
        setLoadingFocus(true);
        const res = await api.get(
          `/vendors/public/rooms/${roomId}/deliverables/${deliverableId}/`
        );
        if (cancelled) return;
        setFocusDeliverable(res.data);
        setStatus((res.data.status || "pending").toLowerCase());
        setIsPaid(Boolean(res.data.isPaid || res.data.paid));
        setIsModalOpen(true);
      } catch (err) {
        if (cancelled) return;
        const statusCode = err?.response?.status;
        toast.error(
          statusCode === 403 || statusCode === 404
            ? "Room is archived or deliverable unavailable"
            : "Failed to load deliverable details"
        );
        setFocusDeliverable(null);
      } finally {
        if (!cancelled) setLoadingFocus(false);
      }
    };

    fetchFocusDeliverable();
    return () => {
      cancelled = true;
    };
  }, [roomId, deliverableId]);

  // 2. Background room grid fetch — unawaited relative to the focus fetch
  // above, so it never blocks the preview modal from loading/opening.
  useEffect(() => {
    let cancelled = false;
    setLoadingRoomList(true);
    api
      .get(`/vendors/public/rooms/${roomId}/deliverables/`)
      .then((res) => {
        if (cancelled) return;
        setRoomDeliverables(res.data?.deliverables || res.data || []);
      })
      .catch(() => {
        if (!cancelled) toast.error("Failed to load room deliverables");
      })
      .finally(() => {
        if (!cancelled) setLoadingRoomList(false);
      });
    return () => {
      cancelled = true;
    };
  }, [roomId]);

  const updateGridItemStatus = (id, nextStatus) => {
    setRoomDeliverables((prev) =>
      prev.map((d) => (d.id === id ? { ...d, status: nextStatus } : d))
    );
  };

  const handleOpenRejectionModal = (item, variant = "reject") => {
    const target = item || focusDeliverable;
    setRejectionTarget(target || null);
    setRejectionVariant(variant);
    setIsRejectionModalOpen(true);
  };
  const handleCloseRejectionModal = () => {
    setIsRejectionModalOpen(false);
    setRejectionTarget(null);
  };

  // Unified status-update call backing approve/reject/download — shared by
  // the focus-modal toolbar and the grid card buttons, so it works whether
  // or not the target deliverable's modal happens to be open.
  const updateStatus = async (
    item,
    nextStatus,
    comment = "",
    successMessage = "Deliverable " + nextStatus
  ) => {
    if (!item) return;
    try {
      await api.patch(
        `/vendors/public/rooms/${roomId}/deliverables/${item.id}/status/?token=${passcode}`,
        { status: nextStatus, comment }
      );
      toast.success(successMessage);
      updateGridItemStatus(item.id, nextStatus);
      if (item.id === focusDeliverable?.id) {
        setStatus(nextStatus);
      }
      if (isRejectionModalOpen) {
        handleCloseRejectionModal();
      }
    } catch (err) {
      toast.error(
        err.response?.data?.error || `Failed to update deliverable to ${nextStatus}`
      );
    }
  };

  // Approve is gated behind a confirmation modal; only confirming it hits
  // the API.
  const handleApprove = (item) => {
    setApproveTarget(item || focusDeliverable || null);
  };
  const handleCancelApprove = () => {
    if (isSubmittingApproval) return;
    setApproveTarget(null);
  };
  const handleConfirmApprove = async () => {
    if (!approveTarget) return;
    setIsSubmittingApproval(true);
    try {
      await updateStatus(approveTarget, "approved");
    } finally {
      setIsSubmittingApproval(false);
      setApproveTarget(null);
    }
  };

  const handleSubmitRejection = async (comment) => {
    if (!rejectionTarget) return;
    setIsSubmittingRejection(true);
    try {
      await updateStatus(
        rejectionTarget,
        "rejected",
        comment,
        rejectionVariant === "changes"
          ? "Change request sent to vendor"
          : undefined
      );
    } finally {
      setIsSubmittingRejection(false);
    }
  };

  const handleDownload = () => {
    if (!focusDeliverable) return;
    toast.success(`Downloading "${focusDeliverable.name}"...`);
    window.location.href = `${api.defaults.baseURL}/vendors/public/rooms/${roomId}/deliverables/${focusDeliverable.id}/download/?token=${passcode}`;
    updateGridItemStatus(focusDeliverable.id, "downloaded");
    setStatus("downloaded");
  };

  // Mock pay — same reasoning as download, no payment gateway wired up yet.
  const handlePay = () => {
    toast.success("Redirecting to payment (mock)...");
    setIsPaid(true);
  };

  const handleCloseModal = () => setIsModalOpen(false);

  // Switching between deliverables already loaded in the background grid
  // just re-navigates the route; the fast-path effect above re-fetches and
  // re-opens the modal for the newly targeted deliverable.
  const handleSelectGridDeliverable = (item) => {
    if (item.id === deliverableId) {
      setIsModalOpen(true);
      return;
    }
    navigate(`/vendors/room/${roomId}/deliverable/${item.id}`);
  };

  const resolvedMime =
    focusDeliverable?.metadata?.mimetype || focusDeliverable?.mimetype || "";

  // preview_url comes straight from the backend response for this
  // deliverable — the client viewer must use it as-is rather than calling
  // the org get_presigned() endpoint or any Firebase Auth helper, since
  // clients viewing a public room link are not Firebase-authenticated.
  const fileInfo = focusDeliverable
    ? {
        id: focusDeliverable.linkedOrgFileId || focusDeliverable.id,
        name: focusDeliverable.name,
        download_url: focusDeliverable.download_url,
        preview_url: focusDeliverable.preview_url,
        mimetype: resolvedMime,
        currVersion: focusDeliverable.linkedOrgFileId || focusDeliverable.id,
        versioning: { versions: [] },
        metadata: { mimetype: resolvedMime },
      }
    : null;

  const visibleDeliverables = filterDeliverables(
    roomDeliverables,
    selectedStatus,
    searchQuery
  );

  return (
    <div
      style={{
        background: theme.bg,
        minHeight: "100vh",
        padding: "32px",
        fontFamily: "inherit",
      }}
    >
      {/* HEADER */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "12px",
          marginBottom: "20px",
        }}
      >
        {/* <button
          onClick={() => navigate(-1)}
          style={{
            background: "transparent",
            border: "none",
            color: theme.textSub,
            fontSize: "13px",
            cursor: "pointer",
            padding: 0,
            marginBottom: "8px",
          }}
        >
          ← Back
        </button> */}
        <h1
          style={{
            fontSize: "22px",
            fontWeight: "700",
            color: theme.textMain,
            margin: 0,
          }}
        >
          Room Deliverables
        </h1>
        <PoweredByPill />
      </div>

      {roomDeliverables.length > 0 && (
        <div style={{ marginBottom: "16px" }}>
          <DeliverableFilterBar
            deliverables={roomDeliverables}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            selectedStatus={selectedStatus}
            setSelectedStatus={setSelectedStatus}
            theme={theme}
          />
        </div>
      )}

      {/* BACKGROUND ROOM GRID */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
          gap: "16px",
        }}
      >
        {loadingRoomList && roomDeliverables.length === 0 ? (
          <p style={{ color: theme.textSub, fontSize: "13px" }}>
            Loading room deliverables...
          </p>
        ) : roomDeliverables.length === 0 ? (
          <p style={{ color: theme.textSub, fontSize: "13px" }}>
            No deliverables in this room yet.
          </p>
        ) : visibleDeliverables.length === 0 ? (
          <p style={{ color: theme.textSub, fontSize: "13px" }}>
            No deliverables match your search or filter.
          </p>
        ) : (
          visibleDeliverables.map((item) => {
            const itemStatus = (item.status || "pending").toLowerCase();
            const gridBadge =
              GRID_STATUS_STYLES[itemStatus] || GRID_STATUS_STYLES.pending;
            const isReviewable = [
              "pending",
              "in review",
              "unapproved",
            ].includes(itemStatus);
            const cardColor = pickCardColor(item.id);
            const displayName = item.name?.split("_TS=")[0];
            return (
              // Mirrors the file card on the dashboard files grid
              // (FilesGridPage) so deliverables look like regular files.
              <div
                key={item.id}
                onClick={() => handleSelectGridDeliverable(item)}
                style={{
                  backgroundColor: hexToRgba(cardColor, 0.1),
                  backgroundImage: FileBg({
                    gradientColors: [cardColor, cardColor, cardColor, cardColor],
                  }),
                  backgroundSize: "cover",
                  backgroundPosition: "center center",
                  backgroundRepeat: "no-repeat",
                  backgroundBlendMode: "lighten",
                  border: "1px solid #ffa9ff46",
                  borderRadius: "16px",
                  padding: "12px",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                <span
                  style={{
                    alignSelf: "flex-start",
                    background: gridBadge.bg,
                    color: gridBadge.text,
                    padding: "3px 8px",
                    borderRadius: "20px",
                    fontSize: "11px",
                    fontWeight: "600",
                  }}
                >
                  {gridBadge.label}
                </span>

                <span
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <img
                    src={getDeliverableIcon(item)}
                    alt="File Preview"
                    onError={(e) => {
                      if (e.currentTarget.src !== DEFAULT_FILE_ICON) {
                        e.currentTarget.src = DEFAULT_FILE_ICON;
                      }
                    }}
                    style={{ borderRadius: "6px", margin: "8px 0" }}
                  />
                </span>

                <div>
                  <h5
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#374151",
                      margin: "0 0 4px 0",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={displayName}
                  >
                    {displayName}
                  </h5>
                  {item.size ? (
                    <span
                      style={{
                        fontSize: "10px",
                        fontWeight: "700",
                        color: "#676767",
                      }}
                    >
                      {item.size}
                    </span>
                  ) : null}
                </div>

                {isReviewable && (
                  <div
                    style={{
                      display: "flex",
                      gap: "6px",
                      marginTop: "2px",
                    }}
                  >
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleApprove(item);
                      }}
                      style={{
                        flex: "1 1 auto",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "4px",
                        background: "#eef2ff",
                        color: "#4f46e5",
                        border: "none",
                        borderRadius: "6px",
                        padding: "6px",
                        whiteSpace: "nowrap",
                        fontSize: "12px",
                        fontWeight: "600",
                        cursor: "pointer",
                      }}
                    >
                      <CheckIcon style={{ fontSize: "14px" }} />
                      Approve
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenRejectionModal(item);
                      }}
                      style={{
                        flex: "1 1 auto",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "4px",
                        background: "transparent",
                        color: "#c5221f",
                        border: "1px solid #c5221f",
                        borderRadius: "6px",
                        padding: "6px",
                        whiteSpace: "nowrap",
                        fontSize: "12px",
                        fontWeight: "600",
                        cursor: "pointer",
                      }}
                    >
                      <CloseIcon style={{ fontSize: "14px" }} />
                      Reject
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenRejectionModal(item, "changes");
                      }}
                      style={{
                        flex: "1 1 auto",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "4px",
                        background: "transparent",
                        color: "#b45309",
                        border: "1px solid #f59e0b",
                        borderRadius: "6px",
                        padding: "6px",
                        whiteSpace: "nowrap",
                        fontSize: "12px",
                        fontWeight: "600",
                        cursor: "pointer",
                      }}
                    >
                      Request changes
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* FOCUS PREVIEW MODAL */}
      {isModalOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            zIndex: 1200,
            overflowY: "auto",
          }}
        >
          <div
            style={{
              background: theme.bg,
              minHeight: "100%",
              height: "100%",
              padding: "32px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "16px",
              }}
            >
              <h2
                style={{
                  fontSize: "20px",
                  fontWeight: "700",
                  color: theme.textMain,
                  margin: 0,
                }}
              >
                {loadingFocus ? "Loading..." : focusDeliverable?.name}
              </h2>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <button
                  type="button"
                  aria-label="Toggle comments"
                  onClick={() => setIsFileCommentsOpen((prev) => !prev)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    background: isFileCommentsOpen ? theme.cardBg : "transparent",
                    border: `1px solid ${theme.border}`,
                    color: theme.textMain,
                    borderRadius: "6px",
                    padding: "0 12px",
                    height: "32px",
                    fontSize: "13px",
                    fontWeight: "600",
                    cursor: "pointer",
                  }}
                >
                  <ChatBubbleOutlineIcon style={{ fontSize: "16px" }} />
                  Comments
                </button>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  style={{
                    background: "#eef2ff",
                    border: "none",
                    color: "#4f46e5",
                    borderRadius: "6px",
                    padding: "0 12px",
                    height: "32px",
                    fontSize: "13px",
                    fontWeight: "600",
                    cursor: "pointer",
                  }}
                >
                  Go to Deliverables Room
                </button>
              </div>
            </div>

            {loadingFocus ? (
              <Loading message="Loading deliverable..." />
            ) : !focusDeliverable || !focusDeliverable.id ? (
              <p style={{ color: theme.textSub, fontSize: "14px" }}>
                Deliverable not found.
              </p>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  width: "100%",
                  height: "calc(100vh - 120px)",
                  gap: "16px",
                  flex: 1,
                  minHeight: 0,
                }}
              >
                <div
                  style={{
                    width: isFileCommentsOpen ? "70%" : "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                    overflow: "hidden",
                    transition: "width 0.2s ease-in-out",
                  }}
                >
                  <ClientActionToolbar
                    status={status}
                    price={focusDeliverable.paywallPrice}
                    isPaid={isPaid}
                    onApprove={() => handleApprove(focusDeliverable)}
                    onReject={() => handleOpenRejectionModal(focusDeliverable)}
                    onRequestChanges={() =>
                      handleOpenRejectionModal(focusDeliverable, "changes")
                    }
                    onDownload={handleDownload}
                    onPay={handlePay}
                  />

                  <ClientFilePreviewer
                    fileInfo={fileInfo}
                    status={status}
                    price={focusDeliverable.paywallPrice}
                    isPaid={isPaid}
                    closeDrawer={handleCloseModal}
                  />
                </div>

                {isFileCommentsOpen && (
                  <div
                    style={{
                      width: "30%",
                      height: "100%",
                      padding: "16px",
                      borderLeft: `1px solid ${theme.border}`,
                      background: theme.cardBg,
                      borderRadius: "8px",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <h3
                      style={{
                        fontSize: "15px",
                        fontWeight: "700",
                        color: "#111827",
                        margin: "0 0 12px 0",
                      }}
                    >
                      Client Feedback
                    </h3>
                    <div style={{ flex: 1, minHeight: 0 }}>
                      <DeliverableChat
                        orgId={focusDeliverable?.org_id}
                        roomId={roomId}
                        deliverableId={focusDeliverable.id}
                        userType="client"
                        token={passcode}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <RejectionModal
        isOpen={isRejectionModalOpen}
        onClose={handleCloseRejectionModal}
        onSubmit={handleSubmitRejection}
        isSubmitting={isSubmittingRejection}
        variant={rejectionVariant}
      />

      <ApproveConfirmModal
        isOpen={!!approveTarget}
        deliverableName={approveTarget?.name}
        onCancel={handleCancelApprove}
        onConfirm={handleConfirmApprove}
        isSubmitting={isSubmittingApproval}
      />
    </div>
  );
}
